<template>
  <div class="editor-root">
    <tag-agents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @click="insertMentionNode"
    />
    <canned-response
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="insertCannedResponse"
    />
    <div
      v-if="shouldShowVariables"
      ref="variableListWrap"
      class="variable-list-wrap"
      @mousedown.prevent
    >
      <variable-list
        :search-key="variableSearchTerm"
        :variable-options="variableOptions"
        :show-close-button="true"
        @click="insertVariable"
        @close="closeVariablesMenu"
      />
    </div>
    <div ref="editor" />
  </div>
</template>

<script>
import {
  messageSchema,
  buildEditor,
  EditorView,
  MessageMarkdownTransformer,
  MessageMarkdownSerializer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import TagAgents from '../conversation/TagAgents';
import CannedResponse from '../conversation/CannedResponse';
import VariableList from '../conversation/VariableList';
import {
  hasPressedEnterAndNotCmdOrShift,
  hasPressedCommandAndEnter,
  hasPressedAltAndPKey,
  hasPressedAltAndLKey,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

const TYPING_INDICATOR_IDLE_TIME = 4000;

/**
 * Match only an *unclosed* `{{...` up to the cursor (no `}}` in between).
 * The stock `triggerCharacters('{{')` regex keeps matching `{{1}}` when the
 * caret is at the end, so the variable picker stays open for every placeholder.
 */
const unclosedDoubleBraceVariableMatcher = $position => {
  const textFrom = $position.before();
  const text = $position.doc.textBetween(textFrom, $position.pos, '\0', '\0');
  const cursorRel = text.length;

  let openIdx = -1;
  for (let i = 0; i < cursorRel - 1; i += 1) {
    if (text[i] === '{' && text[i + 1] === '{') {
      const prefix = i === 0 ? '\0' : text[i - 1];
      if (i === 0 || /[\s\0]/.test(prefix)) {
        openIdx = i;
      }
    }
  }

  if (openIdx === -1) {
    return null;
  }

  const afterOpen = text.slice(openIdx + 2, cursorRel);
  if (afterOpen.includes('}}')) {
    return null;
  }

  const from = textFrom + openIdx;
  const to = $position.pos;
  return { range: { from, to }, text: afterOpen };
};

const createState = (content, placeholder, plugins = []) => {
  return EditorState.create({
    doc: new MessageMarkdownTransformer(messageSchema).parse(content),
    plugins: buildEditor({
      schema: messageSchema,
      placeholder,
      plugins,
    }),
  });
};

export default {
  name: 'WootMessageEditor',
  components: { TagAgents, CannedResponse, VariableList },
  mixins: [eventListenerMixins, uiSettingsMixin],
  props: {
    value: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    isPrivate: { type: Boolean, default: false },
    enableSuggestions: { type: Boolean, default: true },
    overrideLineBreaks: { type: Boolean, default: false },
    updateSelectionWith: { type: String, default: '' },
    enableVariables: { type: Boolean, default: false },
    enableCannedResponses: { type: Boolean, default: true },
    variables: { type: Object, default: () => ({}) },
    // When true, do not focus the editor on mount/reload (e.g. profile settings when navigating to Event ID field)
    disableAutofocus: { type: Boolean, default: false },
  },
  data() {
    return {
      showUserMentions: false,
      showCannedMenu: false,
      showVariables: false,
      mentionSearchKey: '',
      cannedSearchTerm: '',
      variableSearchTerm: '',
      editorView: null,
      range: null,
      state: undefined,
    };
  },
  computed: {
    contentFromEditor() {
      return MessageMarkdownSerializer.serialize(this.editorView.state.doc);
    },
    shouldShowVariables() {
      return this.enableVariables && this.showVariables && !this.isPrivate;
    },
    shouldShowCannedResponses() {
      return (
        this.enableCannedResponses && this.showCannedMenu && !this.isPrivate
      );
    },
    plugins() {
      if (!this.enableSuggestions) {
        return [];
      }

      return [
        suggestionsPlugin({
          matcher: triggerCharacters('@'),
          onEnter: args => {
            this.showUserMentions = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.mentionSearchKey = args.text.replace('@', '');

            return false;
          },
          onExit: () => {
            this.mentionSearchKey = '';
            this.showUserMentions = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showUserMentions;
          },
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('/'),
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showCannedMenu = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.cannedSearchTerm = args.text.replace('/', '');
            return false;
          },
          onExit: () => {
            this.cannedSearchTerm = '';
            this.showCannedMenu = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showCannedMenu;
          },
        }),
        suggestionsPlugin({
          matcher: unclosedDoubleBraceVariableMatcher,
          suggestionClass: '',
          onEnter: args => {
            if (this.isPrivate) {
              return false;
            }
            this.showVariables = true;
            this.range = args.range;
            this.editorView = args.view;
            return false;
          },
          onChange: args => {
            this.editorView = args.view;
            this.range = args.range;

            this.variableSearchTerm = String(args.text || '').replace(
              /^\{\{/,
              ''
            );
            return false;
          },
          onExit: () => {
            this.variableSearchTerm = '';
            this.showVariables = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showVariables;
          },
        }),
      ];
    },
  },
  watch: {
    showUserMentions(updatedValue) {
      this.$emit('toggle-user-mention', this.isPrivate && updatedValue);
    },
    showCannedMenu(updatedValue) {
      this.$emit('toggle-canned-menu', !this.isPrivate && updatedValue);
    },
    showVariables(updatedValue) {
      this.$emit('toggle-variables-menu', !this.isPrivate && updatedValue);
      if (updatedValue) {
        this.$nextTick(() => {
          document.addEventListener('mousedown', this.handleVariableListClickOutside);
        });
      } else {
        document.removeEventListener('mousedown', this.handleVariableListClickOutside);
      }
    },
    value(newValue = '') {
      if (!this.editorView) {
        return;
      }
      // Avoid reloading from parent echo: round-trip mismatch was resetting the doc and caret to the wrong place.
      try {
        const current = MessageMarkdownSerializer.serialize(
          this.editorView.state.doc
        );
        if (current === (newValue || '')) {
          return;
        }
      } catch (e) {
        // fall through to reload
      }
      this.reloadState();
    },
    editorId() {
      this.showCannedMenu = false;
      this.cannedSearchTerm = '';
      this.reloadState();
    },
    isPrivate() {
      this.reloadState();
    },

    updateSelectionWith(newValue, oldValue) {
      if (!this.editorView) {
        return null;
      }
      if (newValue !== oldValue) {
        if (this.updateSelectionWith !== '') {
          const node = this.editorView.state.schema.text(
            this.updateSelectionWith
          );
          const tr = this.editorView.state.tr.replaceSelectionWith(node);
          this.editorView.focus();
          this.state = this.editorView.state.apply(tr);
          this.emitOnChange();
          this.$emit('clear-selection');
        }
      }
      return null;
    },
  },
  created() {
    this.state = createState(this.value, this.placeholder, this.plugins);
  },
  mounted() {
    this.createEditorView();
    this.editorView.updateState(this.state);
    if (!this.disableAutofocus) {
      this.focusEditorInputField();
    }
  },
  methods: {
    reloadState() {
      this.showVariables = false;
      this.variableSearchTerm = '';
      this.state = createState(this.value, this.placeholder, this.plugins);
      this.editorView.updateState(this.state);
      if (!this.disableAutofocus) {
        this.focusEditorInputField();
      }
    },
    createEditorView() {
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.state,
        dispatchTransaction: tx => {
          this.state = this.state.apply(tx);
          this.emitOnChange();
        },
        handleDOMEvents: {
          keyup: () => {
            this.onKeyup();
          },
          keydown: (view, event) => {
            this.onKeydown(event);
          },
          focus: () => {
            this.onFocus();
          },
          blur: () => {
            this.onBlur();
          },
          paste: (view, event) => {
            const data = event.clipboardData && event.clipboardData.files;
            if (!data || data.length === 0) {
              return false;
            }

            const files = Array.from(data);
            const imageFiles = files.filter(
              f => f.type && f.type.startsWith('image/')
            );

            if (imageFiles.length === 0) {
              // Let ProseMirror handle any text/HTML payload on the same
              // paste; ReplyBox picks up the file portion separately.
              return false;
            }

            event.preventDefault();
            event.stopPropagation();

            imageFiles.forEach(file => {
              this.$emit('image-upload', { file, pos: null });
            });

            return true;
          },
          drop: (view, event) => {
            const dataTransfer = event.dataTransfer;
            const data = dataTransfer && dataTransfer.files;
            if (!data || data.length === 0) {
              return false;
            }

            const files = Array.from(data);
            const imageFiles = files.filter(
              f => f.type && f.type.startsWith('image/')
            );
            const otherFiles = files.filter(
              f => !(f.type && f.type.startsWith('image/'))
            );

            if (imageFiles.length === 0) {
              // Let vue-upload-component's dropzone handle non-image drops.
              return false;
            }

            event.preventDefault();
            event.stopPropagation();

            let pos = null;
            try {
              const coords = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });
              pos = coords ? coords.pos : null;
            } catch (e) {
              pos = null;
            }

            imageFiles.forEach(file => {
              this.$emit('image-upload', { file, pos });
            });

            // Forward non-image files alongside the image to the parent.
            if (otherFiles.length > 0) {
              otherFiles.forEach(file => {
                this.$emit('non-image-drop', { file });
              });
            }

            return true;
          },
        },
      });
    },
    // Inserts an <img> node at `pos` (or current selection if invalid).
    insertImageNode({ src, alt = '', pos = null } = {}) {
      if (!this.editorView || !src) {
        return false;
      }
      const { schema, tr } = this.editorView.state;
      if (!schema.nodes.image) {
        return false;
      }
      const node = schema.nodes.image.create({ src, alt });
      let insertPos = pos;
      if (
        insertPos == null ||
        insertPos < 0 ||
        insertPos > tr.doc.content.size
      ) {
        insertPos = this.editorView.state.selection.from;
      }
      const transaction = tr.insert(insertPos, node).scrollIntoView();
      this.editorView.dispatch(transaction);
      this.emitOnChange();
      return true;
    },
    isEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'enter');
    },
    isCmdPlusEnterToSendEnabled() {
      return isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter');
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndPKey(e)) {
        this.focusEditorInputField();
      }
      if (hasPressedAltAndLKey(e)) {
        this.focusEditorInputField();
      }
    },
    focusEditorInputField() {
      const { tr } = this.editorView.state;
      const selection = Selection.atEnd(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    insertMentionNode(mentionItem) {
      if (!this.editorView) {
        return null;
      }
      const node = this.editorView.state.schema.nodes.mention.create({
        userId: mentionItem.id,
        userFullName: mentionItem.name,
      });

      const tr = this.editorView.state.tr
        .replaceWith(this.range.from, this.range.to, node)
        .insertText(` `);
      this.state = this.editorView.state.apply(tr);
      this.emitOnChange();
      this.$track(CONVERSATION_EVENTS.USED_MENTIONS);

      return false;
    },
    insertCannedResponse(cannedItem) {
      const updatedMessage = replaceVariablesInMessage({
        message: cannedItem,
        variables: this.variables,
      });
      if (!this.editorView) {
        return null;
      }

      let from = this.range.from - 1;
      let node = new MessageMarkdownTransformer(messageSchema).parse(
        updatedMessage
      );

      if (node.textContent === updatedMessage) {
        node = this.editorView.state.schema.text(updatedMessage);
        from = this.range.from;
      }

      const tr = this.editorView.state.tr.replaceWith(
        from,
        this.range.to,
        node
      );

      this.state = this.editorView.state.apply(tr);
      this.emitOnChange();

      tr.scrollIntoView();
      this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
      return false;
    },
    /**
     * If the user is editing inside {{ ... }} and the caret is before the closing }},
     * replaceThrough should include those braces so we do not leave a stray "}}" in the doc.
     */
    variableInsertReplaceEnd(doc, from, to) {
      const $to = doc.resolve(Math.min(to, doc.content.size - 1));
      const blockEnd = $to.end();
      if (blockEnd <= to) {
        return to;
      }
      const tail = doc.textBetween(to, blockEnd, '\0', '\0');
      const closeIdx = tail.indexOf('}}');
      if (closeIdx === -1) {
        return to;
      }
      const beforeClose = tail.slice(0, closeIdx);
      if (beforeClose.includes('{{')) {
        return to;
      }
      return to + closeIdx + 2;
    },
    insertVariable(variable) {
      if (!this.editorView) {
        return null;
      }
      this.editorView.focus();
      const state = this.editorView.state;
      const { schema, doc } = state;
      const $from = state.selection.$from;
      const liveMatch = unclosedDoubleBraceVariableMatcher($from);

      // Always anchor to the live caret when not inside an open {{… — never use stale this.range
      // (it often pointed at the document end after focus/blur cycles).
      let from;
      let toBase;
      if (liveMatch) {
        from = liveMatch.range.from;
        toBase = liveMatch.range.to;
      } else {
        const sel = state.selection;
        from = sel.from;
        toBase = sel.to;
      }

      const replaceTo = this.variableInsertReplaceEnd(doc, from, toBase);
      let insertStr = `{{${variable}}}`;
      if (from > 0) {
        const prev = doc.textBetween(from - 1, from, '\0', '\0');
        if (prev && !/\s/.test(prev)) {
          insertStr = ` ${insertStr}`;
        }
      }
      const node = schema.text(insertStr);
      const tr = state.tr.replaceWith(from, replaceTo, node).scrollIntoView();

      this.editorView.dispatch(tr);

      this.showVariables = false;
      this.$track(CONVERSATION_EVENTS.INSERTED_A_VARIABLE);
      return false;
    },

    isNumericVariableInsertionRestricted() {
      if (!this.editorView) return false;
      const { from } = this.editorView.state.selection;
      const doc = this.editorView.state.doc;
      const head = doc.textBetween(0, from, '\0', '\0').replace(/\s+$/, '');
      return head.endsWith('}');
    },

    insertTextAtSelection(text) {
      if (!this.editorView || text == null || text === '') {
        return false;
      }
      this.editorView.focus();
      const state = this.editorView.state;
      let { from, to } = state.selection;
      const doc = state.doc;
      let insertText = String(text);
      if (from > 0) {
        const prev = doc.textBetween(from - 1, from, '\0', '\0');
        if (prev && !/\s/.test(prev)) {
          insertText = ` ${insertText}`;
        }
      }
      if (to < doc.content.size) {
        const next = doc.textBetween(to, to + 1, '\0', '\0');
        if (next && !/\s/.test(next)) {
          insertText = `${insertText} `;
        }
      }
      const node = state.schema.text(insertText);
      const tr = state.tr.replaceWith(from, to, node).scrollIntoView();
      this.editorView.dispatch(tr);
      return true;
    },

    openVariablePicker() {
      if (!this.editorView || this.isPrivate || !this.enableVariables) {
        return;
      }
      this.editorView.focus();
      const { from, to } = this.editorView.state.selection;
      this.range = { from, to };
      this.variableSearchTerm = '';
      this.showVariables = true;
    },

    emitOnChange() {
      this.editorView.updateState(this.state);

      this.$emit('input', this.contentFromEditor);
    },

    hideMentions() {
      this.showUserMentions = false;
    },
    closeVariablesMenu() {
      this.showVariables = false;
    },
    handleVariableListClickOutside(event) {
      const wrap = this.$refs.variableListWrap;
      if (wrap && !wrap.contains(event.target)) {
        this.closeVariablesMenu();
        document.removeEventListener('mousedown', this.handleVariableListClickOutside);
      }
    },
    resetTyping() {
      this.$emit('typing-off');
      this.idleTimer = null;
    },
    turnOffIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
    },
    handleLineBreakWhenEnterToSendEnabled(event) {
      if (
        hasPressedEnterAndNotCmdOrShift(event) &&
        this.isEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    handleLineBreakWhenCmdAndEnterToSendEnabled(event) {
      if (
        hasPressedCommandAndEnter(event) &&
        this.isCmdPlusEnterToSendEnabled() &&
        !this.overrideLineBreaks
      ) {
        event.preventDefault();
      }
    },
    onKeyup() {
      if (!this.idleTimer) {
        this.$emit('typing-on');
      }
      this.turnOffIdleTimer();
      this.idleTimer = setTimeout(
        () => this.resetTyping(),
        TYPING_INDICATOR_IDLE_TIME
      );
    },
    onKeydown(event) {
      if (this.isEnterToSendEnabled()) {
        this.handleLineBreakWhenEnterToSendEnabled(event);
      }
      if (this.isCmdPlusEnterToSendEnabled()) {
        this.handleLineBreakWhenCmdAndEnterToSendEnabled(event);
      }
    },
    onBlur() {
      this.turnOffIdleTimer();
      this.resetTyping();
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleVariableListClickOutside);
  },
};
</script>

<style lang="scss">
@import '@chatwoot/prosemirror-schema/src/styles/base.scss';

.ProseMirror-menubar-wrapper {
  @apply flex flex-col;
  .ProseMirror-menubar {
    min-height: var(--space-two) !important;
    @apply pb-0 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-100;
    z-index: 9;

    .ProseMirror-menu-active {
      @apply bg-slate-75 dark:bg-slate-800;
    }
  }
  > .ProseMirror {
    @apply p-2 break-words text-slate-800 dark:text-slate-100;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-slate-800 dark:text-slate-100;
    }

    blockquote {
      @apply border-slate-400 dark:border-slate-500;

      p {
        @apply text-slate-600 dark:text-slate-400;
      }
    }

    ol li {
      @apply list-item list-decimal;
    }
  }
}

.editor-root {
  @apply w-full relative;
}

.ProseMirror-woot-style {
  @apply overflow-auto min-h-[5rem] max-h-[7.5rem];
}

.ProseMirror-prompt {
  @apply z-50 bg-slate-25 dark:bg-slate-700 rounded-md border border-solid border-slate-75 dark:border-slate-800;
}

.is-private {
  .prosemirror-mention-node {
    @apply font-medium bg-yellow-100 dark:bg-yellow-800 text-slate-900 dark:text-slate-25 py-0 px-1;
  }

  .ProseMirror-menubar-wrapper {
    .ProseMirror-menubar {
      @apply bg-yellow-100 dark:bg-yellow-800 text-slate-700 dark:text-slate-25;
    }

    > .ProseMirror {
      @apply text-slate-800 dark:text-slate-25;

      p {
        @apply text-slate-800 dark:text-slate-25;
      }
    }
  }
}

.editor-wrap {
  @apply mb-4;
}

.message-editor {
  @apply border border-solid border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-md py-0 px-1 mb-0;
}

.editor_warning {
  @apply border border-solid border-red-400 dark:border-red-400;
}

.editor-warning__message {
  @apply text-red-400 dark:text-red-400 font-normal pt-1 pb-0 px-0;
}
</style>
